/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useLazyQuery } from '@apollo/client';
import {
    bool,
    node, shape, string,
} from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {  getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import findContent from '../../../../../gql/queries/findContent';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import useSalesforcePersonalizedContentQuery from '../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';

const PassportBannerContainer = ({ children, selectedSku, isPassportThreshold }) => {
    const isFlagEnabledFlag = useSelector((state) => getFeatureFlag('is-passport-message-from-cs-enabled')(state));
    let ffPassportThreshold = useSelector((state) => getFeatureFlag('is-passport-message-from-cs-enabled')(state));
    const brand = useSelector(getBrand);
    const dispatch = useDispatch();
    const { featureFlags = {} } = useContext(CrossMerch);

    let  isFlagEnabled = false;
    if (Object?.keys(featureFlags)?.length > 0) { // Cross brand products
        isFlagEnabled = featureFlags?.['is-passport-message-from-cs-enabled'];
        ffPassportThreshold = featureFlags?.['is-passport-threshold-pdp-test-enabled'];
    } else { // Default feature flag
        isFlagEnabled = isFlagEnabledFlag;
    }

    const { loading: loadingPassport, variables, data: passportThresholdData } = useSalesforcePersonalizedContentQuery({
        queryName: 'passportThreshold',
        interactionName: `${brand?.domain} - Get Campaign - Passport Threshold`,
        featureFlag: ffPassportThreshold && isPassportThreshold,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    // CONTROL OR VARIANT(TEST) VALUE for Passport Threshold Sales Force Test
    const isPassportThresholdUserGroup = passportThresholdData?.contentSF?.campaign?.campaignResponses?.[0]?.userGroup;
    const passportThresholdVariant = isPassportThresholdUserGroup?.toLowerCase() === 'default';
    const hasWine = selectedSku?.ageVerifyFlag === 'true';
    const query = '\\"title\\": \\"Default\\"';
    const PASSPORT_MESSAGE = findContent({
        brand: 'celebrations',
        contentType: 'passport_copy',
        query,

    });
    if (isPassportThreshold) {
        useClickStreamCustomEventsTracking({
            salesforceResponse: passportThresholdData?.contentSF,
            isFireImpression: true,
            page: 'PDP',
        });
    }

    const [getPassportContent, {
        data, loading, error,
    }] = useLazyQuery(PASSPORT_MESSAGE);

    useEffect(() => {
        if (isFlagEnabled) {
            getPassportContent();
        }
    }, []);

    useEffect(() => {
        if (isPassportThreshold && typeof isPassportThresholdUserGroup !== 'undefined' && !window.wuThresholdEventFired && isPassportThresholdUserGroup) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Passport Threshold',
                eventAction: 'Passport Selected',
                eventLabel: passportThresholdVariant ? 'Variant' : 'Control',
                feature_detail: passportThresholdVariant ? 'Variant' : 'Control',
                feature_element: 'Passport Selected',
                feature_category: 'Passport Threshold',
            };
            dispatch(trackEvent(eventParam));
            window.wuThresholdEventFired = true;
        }
        return () => {
            window.wuThresholdEventFired = false;
        };
    }, [isPassportThresholdUserGroup]);

    const ComponentProps = { messageData: null, hasWine, passportThresholdVariant };
    if (!isFlagEnabled && !data && !loading && !error) {
        return (<>{children}</>);
    }

    if (!data && loading && !error) {
        return <></>;
    }
    const gqlData = data?.findContent?.content?.entries?.[0] || {};
    ComponentProps.fullMessageData = gqlData;
    ComponentProps.pdpMessage = hasWine ? gqlData.pdp_message_wine : gqlData?.pdp_message;

    ComponentProps.showPriceNonMembers = hasWine ? false : gqlData?.display_price_non_members;

    ComponentProps.pdpMessageMember = hasWine ? gqlData.members_pdp_message_wine : gqlData?.members_pdp_message;
    return (<>{React.cloneElement(children, { ...children?.props, ...ComponentProps })}</>);
};

PassportBannerContainer.defaultProps = {
    selectedSku: {
        ageVerifyFlag: 'false', // returning string in GQL response
    },
    isPassportThreshold: false,
};

PassportBannerContainer.propTypes = {
    children: node.isRequired,
    selectedSku: shape({
        ageVerifyFlag: string,
    }),
    isPassportThreshold: bool,
};

export default PassportBannerContainer;
